import React, { useState, useEffect, useCallback } from 'react';
import { Box, useMediaQuery, useTheme, Typography, Slider, CircularProgress, Stack, List, ListItem, ListItemText, Divider, Container, Paper, Fade, Chip } from '@mui/material';
import { PyrenzBlueButton, PyrenzCard } from '@client/theme';
import { Sidebar, MobileNav, PaymentModal, fetchBalanceAPI, fetchTransactionsAPI, Transaction } from '@client/components';
import { useUserStore } from '@client/store';
import { usePyrenzAlert } from '@renderer/provider';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';

interface CreditTier {
  credits: number;
  price: number;
  bonus?: number;
  color: string;
  popular?: boolean;
  maxTokens: number;
  tokenLimitLocked: boolean;
}

const creditTiers: CreditTier[] = [
  {
    credits: 500,
    price: 5,
    color: '#4CAF50',
    maxTokens: 200,
    tokenLimitLocked: true,
  },
  {
    credits: 1000,
    price: 9,
    bonus: 100,
    color: '#2196F3',
    maxTokens: 5000,
    tokenLimitLocked: false,
  },
  {
    credits: 2500,
    price: 20,
    bonus: 500,
    color: '#9C27B0',
    popular: true,
    maxTokens: 5000,
    tokenLimitLocked: false,
  },
  {
    credits: 5000,
    price: 35,
    bonus: 1000,
    color: '#FF9800',
    maxTokens: 5000,
    tokenLimitLocked: false,
  },
  {
    credits: 10000,
    price: 60,
    bonus: 2500,
    color: '#FF5722',
    maxTokens: 5000,
    tokenLimitLocked: false,
  },
];

export function CreditShop() {
  const [selectedTier, setSelectedTier] = useState<CreditTier | null>(null);
  const [selectedCredits, setSelectedCredits] = useState<number>(2500);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const userStore = useUserStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showAlert = usePyrenzAlert();

  const estimatedMessages = balance ?? 0;

  const fetchBalance = useCallback(async () => {
    const userUUID = userStore.userUUID;
    if (!userUUID) {
      setBalance(null);
      setBalanceLoading(false);
      return;
    }

    setBalanceLoading(true);
    try {
      const balanceValue = await fetchBalanceAPI(userUUID);
      setBalance(balanceValue);
      userStore.setCredits(balanceValue);
    } catch (err: any) {
      showAlert(err.message || 'Failed to load balance', 'error');
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, [userStore.userUUID, showAlert, userStore.setCredits]);

  const fetchTransactions = useCallback(async () => {
    const userUUID = userStore.userUUID;
    if (!userUUID) {
      setTransactions([]);
      return;
    }

    setTransactionsLoading(true);
    try {
      const data = await fetchTransactionsAPI(userUUID);
      setTransactions(data);
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  }, [userStore.userUUID]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchBalance(), fetchTransactions()]);
      setInitialFetchDone(true);
    };
    
    loadData();
  }, [fetchBalance, fetchTransactions]);

  useEffect(() => {
    if (initialFetchDone) {
      fetchBalance();
      fetchTransactions();
    }
  }, [userStore.userUUID, initialFetchDone, fetchBalance, fetchTransactions]);

  const handlePurchaseClick = (tier: CreditTier) => {
    if (!userStore.userUUID) {
      setShowLoginModal(true);
      return;
    }
    setSelectedTier(tier);
  };

  const handleCloseModal = () => {
    setSelectedTier(null);
    fetchBalance();
    fetchTransactions();
  };

  const handleCustomPurchase = () => {
    if (!userStore.userUUID) {
      setShowLoginModal(true);
      return;
    }
    const tier = creditTiers.find(t => t.credits === selectedCredits);
    if (tier) setSelectedTier(tier);
  };

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalCredits = (tier: CreditTier) => tier.credits + (tier.bonus || 0);

  const showBalanceLoading = balanceLoading && !initialFetchDone;
  const isLoggedIn = !!userStore.userUUID;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%)',
        display: 'flex',
      }}
    >
      <Sidebar />
      {isMobile && <MobileNav setShowLoginModal={setShowLoginModal} />}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: '72px' },
          minHeight: '100vh',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 4,
            px: { xs: 2, md: 4 },
          }}
        >
          <Fade in timeout={600}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={4}
            >
              {/* Balance Card */}
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  p: 4,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                  <AccountBalanceWalletIcon sx={{ color: '#667eea', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff' }}>
                    Your Balance
                  </Typography>
                </Box>

                {!isLoggedIn ? (
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    Please log in to view your balance
                  </Typography>
                ) : showBalanceLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress color="primary" />
                  </Box>
                ) : (
                  <>
                    <Typography variant="h2" sx={{ color: '#667eea', fontWeight: 700, mb: 1 }}>
                      {(balance ?? 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      ~{estimatedMessages.toLocaleString()} messages remaining
                    </Typography>
                  </>
                )}
              </Paper>

              {/* Credit Packages */}
              <Box sx={{ width: '100%', maxWidth: 1200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <LocalOfferIcon sx={{ color: '#667eea' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                    Credit Packages
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 3,
                  }}
                >
                  {creditTiers.map((tier) => (
                    <Paper
                      key={tier.credits}
                      onClick={() => handlePurchaseClick(tier)}
                      elevation={0}
                      sx={{
                        position: 'relative',
                        p: 3,
                        borderRadius: 3,
                        cursor: 'pointer',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(10px)',
                        border: selectedTier?.credits === tier.credits
                          ? `2px solid ${tier.color}`
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        transition: 'all 300ms ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 20px 40px -10px ${tier.color}40`,
                          borderColor: tier.color,
                        },
                      }}
                    >
                      {tier.popular && (
                        <Chip
                          icon={<StarIcon fontSize="small" />}
                          label="BEST VALUE"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: tier.color,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}

                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: tier.color, mb: 0.5 }}>
                            {getTotalCredits(tier).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            total credits
                          </Typography>
                        </Box>

                        {tier.bonus ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                              {tier.credits.toLocaleString()} base
                            </Typography>
                            <Chip
                              label={`+${tier.bonus.toLocaleString()} bonus`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                color: '#4caf50',
                                fontWeight: 600,
                                height: 20,
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {tier.credits.toLocaleString()} credits
                          </Typography>
                        )}

                        <Box sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Max {tier.maxTokens.toLocaleString()} tokens
                          </Typography>
                          {tier.tokenLimitLocked ? (
                            <Typography variant="caption" sx={{ color: '#ffa726', display: 'block', mt: 0.5 }}>
                              🔒 Token limit locked
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#66bb6a', display: 'block', mt: 0.5 }}>
                              ✓ Adjustable up to 5K
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                            {formatCurrency(tier.price)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {formatCurrency(tier.price / getTotalCredits(tier) * 1000)}/1K
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              </Box>

              {/* Custom Amount */}
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', mb: 3 }}>
                  Custom Amount
                </Typography>
                <Slider
                  value={selectedCredits}
                  onChange={(_, value) => setSelectedCredits(value as number)}
                  min={500}
                  max={10000}
                  step={500}
                  marks={creditTiers.map(t => ({ value: t.credits, label: (t.credits / 1000) + 'K' }))}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => value.toLocaleString()}
                  sx={{
                    color: '#667eea',
                    '& .MuiSlider-thumb': {
                      backgroundColor: '#667eea',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                      {selectedCredits.toLocaleString()} credits
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      {formatCurrency(selectedCredits * 0.008)}
                    </Typography>
                  </Box>
                  <PyrenzBlueButton 
                    onClick={handleCustomPurchase}
                    disabled={!isLoggedIn}
                  >
                    {!isLoggedIn ? 'Login to Purchase' : 'Purchase Now'}
                  </PyrenzBlueButton>
                </Box>
              </Paper>

              {/* Transaction History */}
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 600,
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <HistoryIcon sx={{ color: '#667eea' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
                    Transaction History
                  </Typography>
                </Box>

                {!isLoggedIn ? (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 3 }}>
                    Please log in to view transaction history
                  </Typography>
                ) : transactionsLoading && transactions.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#667eea' }} />
                  </Box>
                ) : transactions.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 3 }}>
                    No transactions yet
                  </Typography>
                ) : (
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {transactions.map((tx, index) => (
                      <React.Fragment key={tx.id}>
                        <ListItem sx={{ py: 1.5 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>
                                  {tx.type === 'purchase' ? 'Purchase' : tx.amount < 0 ? 'Message' : 'Credit'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: tx.amount > 0 ? '#66bb6a' : '#ef5350'
                                  }}
                                >
                                  {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                  {formatDate(tx.created_at)}
                                </Typography>
                                {tx.tokens_used > 0 && (
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                    {tx.tokens_used.toLocaleString()} tokens
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < transactions.length - 1 && (
                          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Box>
          </Fade>
        </Container>
      </Box>

      {selectedTier && (
        <PaymentModal
          plan={{
            title: `${getTotalCredits(selectedTier).toLocaleString()} Credits`,
            price: selectedTier.price,
            baseCredits: selectedTier.credits,
            bonusCredits: selectedTier.bonus,
          }}
          isOpen={!!selectedTier}
          onClose={handleCloseModal}
        />
      )}
    </Box>
  );
}